import { HOTEL_PENSION_TYPE_TRANSLATION_KEYS } from '@/constants/hotelPension';
import type { HotelPensionPricingFormData } from '@/types/benefits/hotel/HotelPensionPricingSchema';
import type { Translator } from '@/types/translator.type';
import type { PastedPensionTariff } from '@/utils/benefits/hotel/excelPaste/parsePensionTariffBlock';
import type { ChildBandsCount } from '@/utils/benefits/hotel/excelPaste/types';
import { getPensionTypesAbove } from '@/utils/estimates/hotel/computeHotelPensionPrice';
import type { ExcelPasteRecapRow } from './ExcelPastePanel';
import {
  buildLabelAndPeriodsRecap,
  buildPastedFormPeriods,
  ChildAges,
  formatRecapAmount,
  FormPeriod,
  getPeriodFieldNames,
  getPersonPriceColumns,
} from './pasteFormHelpers';

export type PensionCard = HotelPensionPricingFormData;
type FormPrice = HotelPensionPricingFormData['prices'][number] & {
  id?: string;
};

const hasValue = (value: unknown) =>
  value !== undefined && value !== null && value !== '';

/** A card added but never filled: no label, no date, no price. */
export const isBlankPensionCard = (card: PensionCard) =>
  !card.name?.trim() &&
  (card.periods ?? []).every(
    (period) => !period.startDate && !period.endDate,
  ) &&
  (card.prices ?? []).every(
    ({ adultPrice, adolescentPrice, childPrice }) =>
      !hasValue(adultPrice) &&
      !hasValue(adolescentPrice) &&
      !hasValue(childPrice),
  );

/**
 * The card once the pasted tariff is written: label, periods, and the prices
 * of each pasted pension type (other pension types keep theirs).
 */
export function mergePastedPensionCard(
  current: PensionCard | undefined,
  pasted: PastedPensionTariff,
): PensionCard {
  const prices = [...((current?.prices ?? []) as FormPrice[])];
  pasted.prices.forEach(({ pensionType, ...amounts }) => {
    const index = prices.findIndex(
      (price) => price.pensionType === pensionType,
    );
    if (index >= 0) prices[index] = { ...prices[index], ...amounts };
    else if (Object.values(amounts).some((amount) => amount !== null)) {
      prices.push({ pensionType, ...amounts });
    }
  });
  return {
    ...current,
    name: pasted.name,
    periods: buildPastedFormPeriods(
      current?.periods as FormPeriod[] | undefined,
      pasted.periods,
    ),
    prices,
  } as PensionCard;
}

/** Field names a pension paste fills in the card, for the highlight. */
export function getPastedPensionFieldNames(
  cardPrefix: string,
  card: PensionCard,
  pasted: PastedPensionTariff,
): string[] {
  return [
    `${cardPrefix}.name`,
    ...getPeriodFieldNames(`${cardPrefix}.periods`, pasted.periods.length),
    ...(card.prices ?? []).flatMap((price, index) =>
      pasted.prices.some(({ pensionType }) => pensionType === price.pensionType)
        ? ['adultPrice', 'adolescentPrice', 'childPrice'].map(
            (field) => `${cardPrefix}.prices.${index}.${field}`,
          )
        : [],
    ),
  ];
}

/** Recap of a pension paste: label, periods, then prices by pension type. */
export function buildPensionRecap(
  current: PensionCard | undefined,
  pasted: PastedPensionTariff,
  {
    basePensionType,
    childBandsCount,
    childAges,
  }: {
    basePensionType?: string;
    childBandsCount: ChildBandsCount;
    childAges: ChildAges;
  },
  t: Translator,
): ExcelPasteRecapRow[] {
  const currentPrices = (current?.prices ?? []) as FormPrice[];
  const columns = getPersonPriceColumns(childBandsCount, childAges, t);
  const shownTypes = getPensionTypesAbove(basePensionType);

  const priceRows = pasted.prices
    .filter(({ pensionType }) => shownTypes.includes(pensionType))
    .flatMap((pastedPrice) => {
      const currentPrice = currentPrices.find(
        ({ pensionType }) => pensionType === pastedPrice.pensionType,
      );
      return columns.map(({ field, label }) => ({
        key: `${pastedPrice.pensionType}-${field}`,
        label: `${t(HOTEL_PENSION_TYPE_TRANSLATION_KEYS[pastedPrice.pensionType])} – ${label}`,
        current: formatRecapAmount(currentPrice?.[field]),
        pasted: formatRecapAmount(pastedPrice[field]),
      }));
    });

  return [
    ...buildLabelAndPeriodsRecap(
      { name: current?.name, periods: current?.periods },
      pasted,
      t,
    ),
    ...priceRows,
  ];
}
